import React from "react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "./ui/dialog";
import { ScrollText } from "lucide-react";

const RULES = [
  {
    title: "The Setup",
    body: "Two teams. Each round, both teams get a SECRET card with a celebrity, character, funny word, or absurd phrase. They also get a side: FOR or AGAINST. The shared question card is the topic everyone debates.",
  },
  {
    title: "Use Your Word or Phrase",
    body: "Every player MUST use their card's word, name, or phrase somewhere inside their debate argument. No skipping it — your card is your character / weapon / muse.",
  },
  {
    title: "The Argument",
    body: "Start the 3-minute Debate Timer. Each player delivers their argument for or against the question, weaving in their secret card. Judges listen.",
  },
  {
    title: "Rebuttals",
    body: "A rebuttal is when a player specifically references and counters something their opponent said inside their own argument. Calling out your opponent's points directly = rebuttal. Encouraged. Spicy.",
  },
  {
    title: "Requesting a Rebuttal",
    body: "If a debater asks the judges for a rebuttal, the judges MUST decide whether to allow it or deny it. Their ruling is final — debaters can request, but only the judges grant.",
  },
  {
    title: "Final Statements",
    body: "After both arguments are done, each player gets a FINAL STATEMENT. Start the 1-minute Final Timer for these. This is the closer — sum up, land your strongest line, drop the mic.",
  },
  {
    title: "The Judges",
    body: "The judges (everyone else in the room) MUST ask at least ONE question per debater about their argument. They can ask more if they feel it's necessary. Be merciless.",
  },
  {
    title: "Scoring",
    body: "After everyone's heard the arguments, rebuttals, finals, and Q&A — vote. The winning team taps + Point. New round, new cards, new madness. First to 10 points wins.",
  },
];

export const RulesDialog = ({ trigger }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <button
            data-testid="open-rules-btn"
            className="btn-brutal-press bg-white text-[#111111] border-4 border-[#111111] shadow-brutal-sm font-anton uppercase text-base md:text-lg px-4 py-2 flex items-center gap-2"
          >
            <ScrollText size={18} /> Rules
          </button>
        )}
      </DialogTrigger>
      <DialogContent
        data-testid="rules-dialog"
        className="max-w-2xl bg-[#FFF4E0] border-4 border-[#111111] shadow-[12px_12px_0px_#111111] rounded-none p-0 max-h-[85vh] overflow-hidden flex flex-col"
      >
        <DialogTitle className="sr-only">Master Debaters Rulebook</DialogTitle>
        <DialogDescription className="sr-only">
          Official rules: word/phrase usage, debate flow, rebuttals, final statements, judges' questions, and scoring.
        </DialogDescription>
        {/* Header */}
        <div className="bg-[#111111] text-[#FFD700] px-6 py-4 border-b-4 border-[#111111]">
          <p className="font-mono-debate font-bold uppercase tracking-[0.3em] text-xs">
            ★ Official Rulebook ★
          </p>
          <h2 className="font-anton uppercase text-4xl md:text-5xl leading-none">
            How to <span className="text-[#FF2A54]">DEBATE</span>
          </h2>
        </div>

        {/* Scrollable rules list */}
        <div className="overflow-y-auto px-6 py-5 space-y-5">
          {RULES.map((r, i) => (
            <div
              key={i}
              data-testid={`rule-item-${i}`}
              className="bg-white border-4 border-[#111111] shadow-brutal-sm p-4"
            >
              <div className="flex items-start gap-3">
                <span className="font-anton uppercase text-2xl bg-[#FFD700] text-[#111111] border-2 border-[#111111] px-2 leading-tight shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-anton uppercase text-xl md:text-2xl text-[#111111] leading-tight">
                    {r.title}
                  </h3>
                  <p className="font-outfit font-medium text-base md:text-lg text-[#111111]/90 mt-1 leading-snug">
                    {r.body}
                  </p>
                </div>
              </div>
            </div>
          ))}

          <div className="bg-[#FF2A54] text-white border-4 border-[#111111] shadow-brutal-sm p-4 text-center">
            <p className="font-anton uppercase text-2xl md:text-3xl leading-tight">
              First Team to 10 Points Wins
            </p>
            <p className="font-mono-debate font-bold uppercase tracking-widest text-[10px] mt-1 opacity-90">
              May the Loudest Argument Win
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
